import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, Inbox, Mail, MessageCircle, Plug, RefreshCw, Send, Sunrise, Trophy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, errorMessage } from "@/lib/api";
import { PendingBadge } from "@/components/Brand";
import { fmtDate } from "@/lib/constants";
import { EmailPreviewDialog } from "@/components/admin/EmailPreviewDialog";

const STATUS_CLS = {
  sent: "bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]",
  pending_provider: "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]",
  failed: "bg-[#FEF2F2] text-[#B91C1C] border-[#FCA5A5]",
  disabled: "bg-zinc-100 text-zinc-500 border-zinc-300",
  skipped: "bg-zinc-100 text-zinc-500 border-zinc-300",
};
const STATUS_LABEL = { sent: "Sent", pending_provider: "Queued · no provider", failed: "Failed", disabled: "Hook off", skipped: "—" };

export function StatusPill({ status, title }) {
  return <span title={title} className={`pill ${STATUS_CLS[status] || STATUS_CLS.disabled}`} data-testid={`notif-status-${status}`}>{STATUS_LABEL[status] || status}</span>;
}

function ChannelCard({ icon: Icon, title, connected, children, testId }) {
  return (
    <div className={`rounded-xl border-2 p-5 ${connected ? "border-black bg-[#DCFCE7]" : "border-dashed border-zinc-500 bg-white"}`} data-testid={testId}>
      <p className="flex items-center gap-2 font-display text-lg font-bold">
        <Icon className="h-5 w-5" /> {title}
        <span className={`pill ml-auto ${connected ? "bg-black text-[#FACC15] border-black" : "bg-zinc-100 text-zinc-600 border-zinc-300"}`}>{connected ? "Connected" : "Not connected"}</span>
      </p>
      <div className="mt-2 text-sm text-zinc-700">{children}</div>
    </div>
  );
}

export function Notifications() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data) });
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testTo, setTestTo] = useState("");
  const s = settings || data?.settings;
  const refresh = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const report = (d, okMsg) => toast[d.status === "sent" ? "success" : "info"](d.status === "sent" ? okMsg : d.error || d.status);

  const save = useMutation({
    mutationFn: () => api.put("/notifications/settings", s).then((r) => r.data),
    onSuccess: (d) => {
      setSettings(d);
      toast.success("Notification settings saved");
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const test = useMutation({
    mutationFn: () => api.post("/notifications/test", { to_email: testTo }).then((r) => r.data),
    onSuccess: (d) => {
      setPreview({ subject: d.subject, body_html: d.body_html, status: d.status, error: d.error, to_email: testTo });
      report(d, `Test email sent to ${testTo}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const digest = useMutation({
    mutationFn: () => api.post("/notifications/digest").then((r) => r.data),
    onSuccess: (d) => {
      report(d, "Digest sent to admin");
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const weekly = useMutation({
    mutationFn: () => api.post("/notifications/weekly-report").then((r) => r.data),
    onSuccess: (d) => {
      report(d, "Weekly report sent to admin");
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const retry = useMutation({
    mutationFn: (id) => api.post(`/notifications/${id}/retry`).then((r) => r.data),
    onSuccess: (d) => {
      report(d, "Email sent");
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const syncInbox = useMutation({
    mutationFn: () => api.post("/inbox/sync").then((r) => r.data),
    onSuccess: (d) => {
      if (d.status === "not_configured") toast.info("Mailbox not connected yet — add the IMAP variables first");
      else if (d.status === "error") toast.error(d.error);
      else toast.success(`Inbox synced · ${d.processed} new email${d.processed === 1 ? "" : "s"}`);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const openPreview = (id) => api.get(`/notifications/${id}`).then((r) => setPreview(r.data)).catch((e) => toast.error(errorMessage(e)));

  if (!data || !s) return <p className="text-zinc-600">Loading…</p>;
  const set = (k) => (e) => setSettings({ ...s, [k]: e.target.value });
  const toggleHook = (ev, v) => setSettings({ ...s, hooks: { ...s.hooks, [ev]: v } });

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2">
        <ChannelCard icon={Mail} title="Email sending (SMTP)" connected={data.provider.connected} testId="email-provider-card">
          {data.provider.smtp?.connected ? (
            <p>Sending via your SMTP server as <strong>{data.provider.smtp.from_email}</strong>. Every subject carries the ticket number so replies thread back automatically.</p>
          ) : data.provider.connected ? (
            <p>SMTP not set yet — falling back to Emergent-managed Resend as <strong>{data.provider.from_name}</strong>. Add these to the backend environment to send from your own mailbox:
              <span className="mt-2 flex flex-wrap gap-1 font-mono text-xs">{data.provider.smtp?.required_env.map((k) => <span key={k} className="rounded bg-zinc-100 px-2 py-1">{k}</span>)}</span>
            </p>
          ) : (
            <p>No sender configured — emails are rendered and queued.</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
            <span className="pill bg-[#DCFCE7] border-[#86EFAC] text-[#15803D]" data-testid="notif-count-sent">{data.counts.sent} sent</span>
            <span className="pill bg-[#FEF3C7] border-[#FCD34D] text-[#B45309]" data-testid="notif-count-queued">{data.counts.pending_provider} queued</span>
            <span className="pill bg-[#FEF2F2] border-[#FCA5A5] text-[#B91C1C]">{data.counts.failed} failed</span>
          </div>
        </ChannelCard>
        <ChannelCard icon={Inbox} title="Incoming mail (IMAP)" connected={Boolean(data.inbox?.connected)} testId="imap-provider-card">
          {data.inbox?.connected ? (
            <p>Polling <strong>{data.provider.imap?.mailbox}</strong> every 2 minutes. Replies are matched by ticket number (then sender email) and attached to the conversation; unmatched emails become new tickets.</p>
          ) : (
            <>
              <p>Customer replies will be pulled into ticket conversations once you add:</p>
              <ul className="mt-2 space-y-1 font-mono text-xs">{data.provider.imap?.required_env.map((k) => <li key={k} className="rounded bg-zinc-100 px-2 py-1">{k}</li>)}</ul>
              <div className="mt-3"><PendingBadge label="Awaiting IMAP credentials" testId="imap-pending-badge" /></div>
            </>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <button type="button" className="btn-ghost !py-1 !text-xs" onClick={() => syncInbox.mutate()} disabled={syncInbox.isPending} data-testid="notif-inbox-sync-button">
              <RefreshCw className={`h-3.5 w-3.5 ${syncInbox.isPending ? "animate-spin" : ""}`} /> Sync inbox now
            </button>
            {data.inbox?.last_sync_at && <span className="text-zinc-500">Last sync {fmtDate(data.inbox.last_sync_at)}</span>}
            {data.inbox?.last_error && <span className="text-[#B91C1C]" data-testid="inbox-last-error">{data.inbox.last_error}</span>}
          </div>
        </ChannelCard>
        <ChannelCard icon={MessageCircle} title="WhatsApp" connected={data.whatsapp.connected} testId="whatsapp-provider-card">
          {data.whatsapp.connected ? (
            <p>Sending via Twilio from number ending {data.whatsapp.from}. Customers get the same updates as email; admin alerts go to the phone below.</p>
          ) : (
            <>
              <p>Ready to go — add your Twilio keys to the backend environment and messages start flowing automatically:</p>
              <ul className="mt-2 space-y-1 font-mono text-xs">
                {data.whatsapp.required_env.map((k) => <li key={k} className="rounded bg-zinc-100 px-2 py-1">{k}</li>)}
              </ul>
              <div className="mt-3"><PendingBadge label="Awaiting Twilio keys" testId="whatsapp-pending-badge" /></div>
            </>
          )}
        </ChannelCard>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="grid gap-6 lg:grid-cols-[1fr_380px]"
        data-testid="notification-settings-form"
      >
        <section className="brut-card space-y-4 p-5 sm:p-6">
          <h3 className="font-display text-xl font-bold">Triggers</h3>
          <ul className="divide-y divide-zinc-200">
            {data.hooks.map((h) => (
              <li key={h.event} className="flex items-start justify-between gap-4 py-3" data-testid={`hook-${h.event}`}>
                <div>
                  <p className="font-semibold">{h.label} <span className="ml-1 rounded-full border border-black px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">to {h.recipient}</span></p>
                  <p className="text-sm text-zinc-600">{h.description}</p>
                </div>
                <Switch checked={Boolean(s.hooks[h.event])} onCheckedChange={(v) => toggleHook(h.event, v)} className="mt-1 shrink-0 data-[state=checked]:bg-black" data-testid={`hook-toggle-${h.event}`} />
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-dashed border-zinc-400 p-3 text-sm">
            <Sunrise className="h-5 w-5" />
            <span>Digest runs automatically at <strong>09:30 IST, Mon–Sat</strong>.</span>
            <button type="button" className="btn-ghost ml-auto !py-1.5 !text-xs" onClick={() => digest.mutate()} disabled={digest.isPending} data-testid="send-digest-now-button">
              {digest.isPending ? "Sending…" : "Send digest now"}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-dashed border-zinc-400 p-3 text-sm">
            <Trophy className="h-5 w-5" />
            <span>Weekly performance report goes out <strong>Monday 09:00 IST</strong>.</span>
            <button type="button" className="btn-ghost ml-auto !py-1.5 !text-xs" onClick={() => weekly.mutate()} disabled={weekly.isPending} data-testid="send-weekly-report-button">
              {weekly.isPending ? "Sending…" : "Send weekly report now"}
            </button>
          </div>
        </section>
        <section className="brut-card brut-shadow h-fit space-y-4 p-5">
          <h3 className="flex items-center gap-2 font-display text-xl font-bold"><Plug className="h-5 w-5" /> Recipients & reply-to</h3>
          <div>
            <label className="field-label">Reply-to for customer emails</label>
            <input className="field-input" type="email" placeholder="support@yourdomain.com (optional)" value={s.reply_to} onChange={set("reply_to")} data-testid="reply-to-input" />
          </div>
          <div>
            <label className="field-label">Admin alert email</label>
            <input className="field-input" type="email" value={s.admin_alert_email} onChange={set("admin_alert_email")} data-testid="admin-alert-email-input" />
          </div>
          <div>
            <label className="field-label">Admin alert WhatsApp number</label>
            <input className="field-input" placeholder="+91 98765 43210" value={s.admin_alert_phone} onChange={set("admin_alert_phone")} data-testid="admin-alert-phone-input" />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={save.isPending} data-testid="save-notification-settings-button">
            {save.isPending ? "Saving…" : "Save settings"}
          </button>
          <div className="border-t-2 border-dashed border-zinc-300 pt-4">
            <label className="field-label">Send a test email</label>
            <div className="flex gap-2">
              <input className="field-input !py-2.5" type="email" placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="test-email-input" />
              <button type="button" className="btn-dark !py-2.5" onClick={() => test.mutate()} disabled={!testTo || test.isPending} data-testid="send-test-email-button">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>
      </form>

      <section>
        <h3 className="mb-3 font-display text-xl font-bold">Outbox</h3>
        {data.events.length === 0 ? (
          <p className="text-sm text-zinc-600" data-testid="notification-events-empty">No notifications triggered yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border-2 border-black bg-white">
            <table className="w-full text-left text-sm" data-testid="notification-events-table">
              <thead className="bg-black text-[11px] uppercase tracking-wider text-white">
                <tr>
                  <th className="px-4 py-2.5">When</th>
                  <th className="px-4 py-2.5">Ticket</th>
                  <th className="px-4 py-2.5">Subject</th>
                  <th className="hidden px-4 py-2.5 md:table-cell">To</th>
                  <th className="px-4 py-2.5">Email</th>
                  <th className="hidden px-4 py-2.5 sm:table-cell">WhatsApp</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {data.events.map((e) => (
                  <tr key={e.id} data-testid={`notif-row-${e.id}`}>
                    <td className="whitespace-nowrap px-4 py-2.5 text-zinc-600">{fmtDate(e.created_at)}</td>
                    <td className="px-4 py-2.5 font-mono font-bold">{e.ticket_id || "—"}</td>
                    <td className="max-w-[260px] truncate px-4 py-2.5" title={e.subject}>{e.subject}</td>
                    <td className="hidden px-4 py-2.5 md:table-cell">{e.to_email || <span className="text-zinc-400">—</span>}</td>
                    <td className="px-4 py-2.5"><StatusPill status={e.status} title={e.error} /></td>
                    <td className="hidden px-4 py-2.5 sm:table-cell"><StatusPill status={e.whatsapp_status || "skipped"} title={e.whatsapp_error} /></td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        <button className="btn-ghost !px-2.5 !py-1.5 !text-xs" onClick={() => openPreview(e.id)} data-testid={`notif-preview-${e.id}`}><Eye className="h-3.5 w-3.5" /></button>
                        {(e.status === "failed" || e.status === "pending_provider" || e.whatsapp_status === "failed" || e.whatsapp_status === "pending_provider") && (
                          <button className="btn-ghost !px-2.5 !py-1.5 !text-xs" onClick={() => retry.mutate(e.id)} disabled={retry.isPending} title="Retry" data-testid={`notif-retry-${e.id}`}><RefreshCw className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <EmailPreviewDialog preview={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
